import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useGame } from '../context/GameContext';
import { normalizeTag } from '../game/profile';
import { PERK_LIST, PERK_BRANCHES, PERKS, LOADOUT_PRESETS } from '../game/perks';
import { ArrowLeft, Zap, Shield, Star, Award, Check, Lock, Sparkles, Rocket } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCH_META = {
  aggression: { label: 'AGGRESSION', color: '#ff2b2b', Icon: Zap,   desc: 'Damage, energy, opening moves.' },
  sustain:    { label: 'SUSTAIN',    color: '#39ff14', Icon: Shield,desc: 'Max HP, healing, defenses.' },
  utility:    { label: 'UTILITY',    color: '#ffd700', Icon: Star,  desc: 'Gold, draws, rewards, shops.' },
};

// v3.5 STARTER LOADOUTS: recommended tier-1 perk per class.
// Chosen to match class fantasy: RIZZLER = burst damage, SIGMA = economy, GOONER = tank HP.
const RECOMMENDED_STARTER = {
  rizzler: { perkId: 'aggression_start_str', reason: 'as a RIZZLER, +1 STR every combat snowballs your rizz-attack scaling.' },
  sigma:   { perkId: 'utility_start_gold',   reason: 'as a SIGMA, +50 gold every run funds your lone-wolf shop plays.' },
  gooner:  { perkId: 'sustain_max_hp',       reason: 'as a GOONER, +10 max HP keeps you alive through every party.' },
};
const DEFAULT_RECOMMENDED = RECOMMENDED_STARTER.rizzler;

// v3.3 TALENT TREE screen — spend PERK POINTS on persistent metagame perks.
export default function Perks() {
  const { goToMenu, playerName, profile, refreshProfile } = useGame();
  const [unlocking, setUnlocking] = useState(null);
  const [err, setErr] = useState('');
  const [respec, setRespec] = useState(false);

  const perkPoints = profile?.perk_points || 0;
  const ownedPerks = new Set(profile?.perks_owned || []);
  const tokens = profile?.slop_tokens || 0;
  const RESPEC_COST = 150;

  // v3.5 STARTER LOADOUTS: pick recommendation from highest-mastery class, else default.
  const recommended = useMemo(() => {
    const masteries = profile?.masteries || {};
    let bestClass = null;
    let bestVal = -1;
    for (const [cls, val] of Object.entries(masteries)) {
      if ((val || 0) > bestVal) { bestClass = cls; bestVal = val || 0; }
    }
    return (bestClass && RECOMMENDED_STARTER[bestClass]) || DEFAULT_RECOMMENDED;
  }, [profile]);
  const recommendedOwned = ownedPerks.has(recommended.perkId);

  const unlock = async (perkId) => {
    setErr('');
    const tag = normalizeTag(playerName);
    if (!tag) { setErr('SET A PLAYER TAG FIRST'); return; }
    setUnlocking(perkId);
    try {
      const r = await axios.patch(`${API}/profile/${encodeURIComponent(tag)}`, { perk_unlock: perkId });
      if (r.data) await refreshProfile();
    } catch (e) {
      setErr(e.response?.data?.detail || 'UNLOCK FAILED');
    } finally {
      setUnlocking(null);
    }
  };

  const doRespec = async () => {
    setErr('');
    const tag = normalizeTag(playerName);
    if (!tag) { setErr('SET A PLAYER TAG FIRST'); return; }
    if (!window.confirm(`Refund ALL owned perks for ${RESPEC_COST} tokens? You keep the PP but lose all unlocks.`)) return;
    setRespec(true);
    try {
      await axios.post(`${API}/profile/${encodeURIComponent(tag)}/respec`);
      await refreshProfile();
    } catch (e) {
      setErr(e.response?.data?.detail || 'RESPEC FAILED');
    } finally {
      setRespec(false);
    }
  };

  // v3.6 LOADOUT PRESETS: sequentially unlock every unowned perk in the preset (respects prereqs
  // because perkIds are declared in dependency order). Stops early if PP runs out; user keeps whatever unlocked.
  const [applyingPreset, setApplyingPreset] = useState(null);
  const [applyProgress, setApplyProgress] = useState({ current: 0, total: 0 });
  const applyPreset = async (preset) => {
    setErr('');
    const tag = normalizeTag(playerName);
    if (!tag) { setErr('SET A PLAYER TAG FIRST'); return; }
    const missing = preset.perkIds.filter(id => !ownedPerks.has(id));
    if (missing.length === 0) { setErr(`ALREADY OWN ALL ${preset.name} PERKS`); return; }
    if (perkPoints < missing.length) {
      setErr(`NEED ${missing.length} PP FOR ${preset.name} (YOU HAVE ${perkPoints})`);
      return;
    }
    setApplyingPreset(preset.id);
    setApplyProgress({ current: 0, total: missing.length });
    try {
      for (let i = 0; i < missing.length; i++) {
        await axios.patch(`${API}/profile/${encodeURIComponent(tag)}`, { perk_unlock: missing[i] });
        setApplyProgress({ current: i + 1, total: missing.length });
      }
      await refreshProfile();
    } catch (e) {
      setErr(e.response?.data?.detail || 'PRESET APPLY FAILED');
    } finally {
      setApplyingPreset(null);
      setApplyProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="min-h-screen w-full px-4 md:px-6 py-6 max-w-6xl mx-auto" data-testid="perks-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button className="brutal-btn text-sm" onClick={goToMenu} data-testid="perks-back-button">
          <ArrowLeft size={16} /> BACK
        </button>
        <h1 className="font-display text-3xl sm:text-5xl font-black uppercase text-[color:var(--neon)] flex items-center gap-3">
          <Star /> TALENT TREE
        </h1>
        <div className="brutal-border-pink px-3 py-2 flex items-center gap-2" data-testid="perks-points">
          <Award size={16} className="text-[color:var(--pink)]"/>
          <span className="font-display font-black text-xl text-[color:var(--pink)]">{perkPoints} PP</span>
        </div>
      </div>

      <div className="mt-4 flex flex-col items-center gap-2">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] text-center">
          earn +1 PERK POINT per boss killed · spend on permanent metagame buffs · tier N requires tier N-1
        </div>
        <button
          className="brutal-btn text-xs px-3 py-1.5 mt-1"
          onClick={doRespec}
          disabled={respec || ownedPerks.size === 0 || tokens < RESPEC_COST}
          style={{ borderColor: '#c084fc', color: '#c084fc', boxShadow: '3px 3px 0 #c084fc' }}
          data-testid="perks-respec-button"
        >
          {respec ? 'RESPECCING...' : `♻ RESPEC · ${RESPEC_COST} 🪙 (refund ${ownedPerks.size} perks)`}
        </button>
      </div>

      {err && <div className="mt-4 brutal-border-pink p-3 text-[color:var(--red)] uppercase text-center" data-testid="perks-error">{err}</div>}

      {/* v3.5 STARTER LOADOUTS: one-click recommended perk for new players */}
      <div
        className="mt-6 p-4 brutal-border-dim flex flex-col md:flex-row items-start md:items-center gap-3 justify-between"
        style={{ borderColor: '#00e5ff', boxShadow: '4px 4px 0 #00e5ff', background: '#001418' }}
        data-testid="perks-recommended-panel"
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Sparkles size={22} className="mt-0.5" style={{ color: '#00e5ff' }} />
          <div className="min-w-0">
            <div className="font-display font-black uppercase text-lg" style={{ color: '#00e5ff' }} data-testid="perks-recommended-title">
              STARTER LOADOUT · {(PERKS[recommended.perkId]?.name) || recommended.perkId.split('_').join(' ').toUpperCase()}
            </div>
            <div className="text-xs text-white/85 uppercase tracking-wide mt-1" data-testid="perks-recommended-reason">
              {recommended.reason}
            </div>
          </div>
        </div>
        <button
          className="brutal-btn text-sm px-4 py-2"
          onClick={() => unlock(recommended.perkId)}
          disabled={recommendedOwned || perkPoints < 1 || unlocking === recommended.perkId}
          style={{
            borderColor: recommendedOwned ? '#39ff14' : '#00e5ff',
            color: recommendedOwned ? '#39ff14' : '#00e5ff',
            boxShadow: recommendedOwned ? '3px 3px 0 #39ff14' : '3px 3px 0 #00e5ff',
          }}
          data-testid="perks-use-recommended-button"
        >
          {recommendedOwned ? <><Check size={14}/> ALREADY OWNED</> :
            unlocking === recommended.perkId ? 'UNLOCKING...' :
            perkPoints < 1 ? <><Lock size={14}/> NEED 1 PP</> :
            <><Award size={14}/> USE RECOMMENDED (1 PP)</>}
        </button>
      </div>

      {/* v3.6 LOADOUT PRESETS — one-click multi-perk builds */}
      <div className="mt-6" data-testid="perks-loadouts-section">
        <div className="flex items-center gap-2 mb-3">
          <Rocket size={18} className="text-[color:var(--pink)]"/>
          <h2 className="font-display font-black uppercase text-lg text-[color:var(--pink)]">LOADOUT PRESETS</h2>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)]">· one-click meta builds</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {LOADOUT_PRESETS.map(preset => {
            const missing = preset.perkIds.filter(id => !ownedPerks.has(id));
            const fullyOwned = missing.length === 0;
            const need = missing.length;
            const canAfford = perkPoints >= need;
            const isApplying = applyingPreset === preset.id;
            return (
              <div
                key={preset.id}
                className="p-3 bg-[#0a0a0a] flex flex-col gap-2 menu-section-btn"
                style={{
                  border: `2px solid ${preset.hue}`,
                  boxShadow: `4px 4px 0 ${preset.hue}`,
                  ['--hue']: preset.hue,
                }}
                data-testid={`perks-preset-${preset.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display font-black uppercase text-base" style={{ color: preset.hue }} data-testid={`perks-preset-name-${preset.id}`}>
                    {preset.name}
                  </div>
                  <div className="text-[10px] uppercase font-mono tracking-widest" style={{ color: preset.hue, opacity: 0.75 }}>
                    {preset.perkIds.length} PERKS
                  </div>
                </div>
                <div className="text-[10px] uppercase font-mono tracking-wide text-white/70">{preset.tagline}</div>
                <div className="text-xs text-white/80 leading-snug">{preset.desc}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {preset.perkIds.map(pid => {
                    const owned = ownedPerks.has(pid);
                    return (
                      <span
                        key={pid}
                        className="text-[9px] uppercase font-mono px-1.5 py-0.5 border"
                        style={{
                          borderColor: owned ? preset.hue : '#333',
                          color: owned ? preset.hue : '#888',
                          background: owned ? `${preset.hue}18` : 'transparent',
                        }}
                        data-testid={`perks-preset-chip-${preset.id}-${pid}`}
                      >
                        {owned && '✓ '}{PERKS[pid]?.name || pid}
                      </span>
                    );
                  })}
                </div>
                <button
                  className="brutal-btn text-xs px-3 py-2 mt-1 w-full"
                  onClick={() => applyPreset(preset)}
                  disabled={fullyOwned || !canAfford || isApplying}
                  style={{
                    borderColor: fullyOwned ? '#39ff14' : preset.hue,
                    color:       fullyOwned ? '#39ff14' : preset.hue,
                    boxShadow:   fullyOwned ? '3px 3px 0 #39ff14' : `3px 3px 0 ${preset.hue}`,
                  }}
                  data-testid={`perks-preset-apply-${preset.id}`}
                >
                  {isApplying ? `APPLYING ${applyProgress.current}/${applyProgress.total}...` :
                    fullyOwned ? <><Check size={12}/> FULLY OWNED</> :
                    !canAfford ? <><Lock size={12}/> NEED {need} PP ({perkPoints} HAVE)</> :
                    <><Rocket size={12}/> APPLY ({need} PP)</>}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {PERK_BRANCHES.map(branch => {
          const meta = BRANCH_META[branch];
          const branchPerks = PERK_LIST.filter(p => p.branch === branch).sort((a, b) => a.tier - b.tier);
          const Icon = meta.Icon;
          return (
            <div
              key={branch}
              className="p-4 brutal-border-dim bg-[#0a0a0a] flex flex-col gap-3"
              style={{ borderColor: meta.color, boxShadow: `4px 4px 0 ${meta.color}` }}
              data-testid={`perks-branch-${branch}`}
            >
              <div className="flex items-center gap-2">
                <Icon size={18} style={{ color: meta.color }} />
                <div className="font-display font-black uppercase text-xl" style={{ color: meta.color }}>{meta.label}</div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)]">{meta.desc}</div>
              <div className="flex flex-col gap-2 mt-1">
                {branchPerks.map(perk => {
                  const isOwned = ownedPerks.has(perk.id);
                  const canAfford = perkPoints >= 1;
                  const prereqOk = !perk.requires || ownedPerks.has(perk.requires);
                  const isLocked = !isOwned && !prereqOk;
                  return (
                    <div
                      key={perk.id}
                      className="p-3 border-2"
                      style={{
                        borderColor: isOwned ? meta.color : isLocked ? '#222' : '#333',
                        background: isOwned ? '#0a0800' : '#050505',
                        boxShadow: isOwned ? `3px 3px 0 ${meta.color}` : 'none',
                        opacity: isOwned ? 1 : isLocked ? 0.4 : 0.9,
                      }}
                      data-testid={`perks-slot-${perk.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="text-2xl">{perk.art}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-black uppercase text-sm" style={{ color: isOwned ? meta.color : '#ccc' }}>
                            {perk.name}
                            <span className="ml-2 text-[9px] opacity-60">TIER {perk.tier}</span>
                          </div>
                          <div className="text-xs text-white/85 mt-1 leading-snug">{perk.desc}</div>
                          {isLocked && (
                            <div className="text-[9px] uppercase font-mono tracking-widest text-[color:var(--text-dim)] mt-1 flex items-center gap-1" data-testid={`perks-locked-${perk.id}`}>
                              <Lock size={9}/> REQUIRES TIER {perk.tier - 1}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-2">
                        {isOwned ? (
                          <div className="text-[10px] uppercase font-mono tracking-widest flex items-center gap-1" style={{ color: meta.color }}>
                            <Check size={12}/> UNLOCKED
                          </div>
                        ) : (
                          <button
                            className="brutal-btn text-xs px-2 py-1 w-full"
                            onClick={() => unlock(perk.id)}
                            disabled={!canAfford || isLocked || unlocking === perk.id}
                            style={{ borderColor: !isLocked && canAfford ? meta.color : '#444', color: !isLocked && canAfford ? meta.color : '#666' }}
                            data-testid={`perks-unlock-${perk.id}`}
                          >
                            {unlocking === perk.id ? 'UNLOCKING...' : isLocked ? <><Lock size={12}/> LOCKED</> : canAfford ? <><Award size={12}/> UNLOCK (1 PP)</> : <><Lock size={12}/> NEED 1 PP</>}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
